import type { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import type { Response } from '../types/common.ts';

import axios from 'axios';

import { config } from '../config.ts';
import { getAccessToken, setAccessToken } from '../utils/cookies.ts';
import { handleMiaUnauthorized } from './mia-session.ts';

const instanceConfigDefaults = {
  baseURL: config.API_BASE_URL,
  headers: {
    Accept: 'application/json',
  },
};

const api = axios.create(instanceConfigDefaults);
const auth = axios.create({
  ...instanceConfigDefaults,
  baseURL: config.AUTH_BASE_URL,
});
const mia = axios.create({
  ...instanceConfigDefaults,
  baseURL: config.MIA_API_BASE_URL,
  // Array params travel as plain repeated keys (providerIds=1&providerIds=2), while scalar values
  // such as providerIds=all remain unchanged. The Mia API is Express 5, whose "simple" query
  // parser keeps axios's default bracket form as a literal `providerIds[]` key.
  paramsSerializer: { indexes: null },
});

function useTokenInStorage(config: InternalAxiosRequestConfig) {
  const token = getAccessToken();
  if (token) {
    config.headers['X-Access-Token'] = token;
  }
  return config;
}

// Update the token cookie each time we receive a response
function updateTokenStorage(response: AxiosResponse<Response>) {
  // Skip setting the cookie when no token exists or the request opts out
  if (!response.data.token || response.config.suppressTokenUpdate) {
    return response;
  }

  setAccessToken(response.data.token);
  return response;
}

/**
 * Update the expiration date saved in the store any time we receive a response
 * with a token.
 */
// function updateTokenExpirationDate(response: AxiosResponse<Response>) {
//   if (!response?.data?.token) {
//     return response;
//   }

//   try {
//     const rawPayload = response.data.token.split('.')[1] as string;
//     const payload = JSON.parse(atob(rawPayload)) as { exp: number };
//     /**
//      * The expiration date comes in seconds so we need to convert it to
//      * milliseconds to easily perform operations with date objects. We also
//      * substract 60 seconds to ensure we give it plenty of time to be refreshed
//      * before it expires. The value of `expiresAt` will roughly be 1 hour and
//      * 59 minutes after the token is received based on an expiration of 2 hours.
//      */
//     const expiresAt = payload.exp * 1_000 - 60_000;
//     useRefreshTokenStore.getState().setExpiresAt(expiresAt);
//   } catch (error) {
//     return response;
//   }

//   return response;
// }

/**
 * Sometimes the API responds with a 200 even if the request fails, we check
 * for that here and throw an error explicitly.
 */
function failOn200WithErrorMessage(response: AxiosResponse<Response>) {
  const errorMessage = Array.isArray(response.data.messages)
    ? response.data.messages.find((message) => message.type === 'Error' && message.id !== 409)
    : undefined;

  if (errorMessage) {
    throw new Error(errorMessage.text);
  }

  return response;
}

/**
 * Mia's 401 identifies a rejected shared session, unlike the legacy API's ambiguous 403.
 * Preserve the original error so callers still handle non-auth failures normally.
 */
function redirectOnMiaUnauthorized(error: unknown): never {
  if (axios.isAxiosError(error) && error.response?.status === 401) {
    handleMiaUnauthorized();
  }

  if (error instanceof Error) {
    throw error;
  }

  throw new Error(`Mia request failed with a non-Error rejection: ${String(error)}`);
}

api.interceptors.request.use(useTokenInStorage);
// The Mia API only shares authentication with the legacy API — it never rotates tokens in
// response bodies and reports failures through real HTTP status codes instead of 200s with an
// error-message envelope, so the response interceptors below don't apply to it.
mia.interceptors.request.use(useTokenInStorage);
mia.interceptors.response.use(undefined, redirectOnMiaUnauthorized);
api.interceptors.response.use(updateTokenStorage);
// api.interceptors.response.use(updateTokenExpirationDate);
api.interceptors.response.use(failOn200WithErrorMessage);

export { api, auth, mia };
